/**
 * The one status-change dialog (ADR 0006).
 *
 * It exists because there used to be two ways to move an order: this modal on
 * the detail page, which asked for a reason and offered the per-line restock
 * decisions, and a bare popover on the Orders list that posted an empty comment
 * and moved no stock. Both surfaces mount this now, so a cancellation from a
 * table row is the same transaction as one from the order page.
 *
 * Three rules live here, and each is enforced again on the server:
 *   - the exits (Cancelled / Returned / On hold) need a typed reason;
 *   - On hold needs a category, and remembers where it was held from;
 *   - cancelling or returning an order that has finished goods needs a
 *     Sellable/Damaged decision per line, or the stock silently disappears.
 *     "Has finished goods" is resolved through heldFromStatus for a held order,
 *     not from the HOLD status itself — see restocksFrom().
 */

import React, { useEffect, useMemo, useState } from 'react';
import { ConfigProvider, Input, Modal, Select } from 'antd';
import {
  SwapOutlined,
  UndoOutlined,
  CheckCircleFilled,
  InfoCircleOutlined,
} from '@ant-design/icons';
import {
  EXIT_STATUSES,
  requiresReason,
  restocksFrom,
  settableStatuses,
} from '../../utils/enums/OrderStatusEnum';
import { useStateContext } from '../../contexts/ContextProvider';
import {
  PRIMARY,
  SHELL_THEME,
  STATUS,
  MONO,
  shellAntdTheme,
  shellVars,
  hexToRgba,
  ringOf,
} from '../../shellTokens';
import {
  fmtBDT,
  statusMeta,
  HOLD_REASONS,
  StatusBadge,
} from './orderPrimitives';
import '../Util/formControls.css';

const HOLD = 11;
const RETURNED = 7;
const CANCELLED = 8;

/** Safely normalizes any status value (number, numeric string) to integer, or null. */
const parseStatus = (val) => {
  if (val === null || val === undefined || val === '') return null;
  if (typeof val === 'object') return null; // guards against any DOM/React SyntheticEvent
  const n = Number(val);
  return isNaN(n) ? null : n;
};

/**
 * A line's one-line identity in the restock list. ProductResource sends the
 * type/fabric fields as raw ids, so the names are only there when a caller has
 * hydrated them — the description is the reliable human label, and the id is the
 * last resort.
 */
const lineLabel = (p) => {
  const named = [p.productType?.name, p.fabrics?.name, p.fabricType?.name]
    .filter(Boolean)
    .join(' · ');
  return named || p.description || `Line ${p.id}`;
};

export default function StatusChangeModal({
  open,
  order,
  permissions,
  initialStatus = null,
  onCancel,
  onSubmit,
  submitting = false,
}) {
  const { darkMode } = useStateContext();
  const themeVars = useMemo(() => ({
    ...shellVars(darkMode),
    '--focusRing': ringOf(PRIMARY),
  }), [darkMode]);
  const t = SHELL_THEME[darkMode ? 'dark' : 'light'];

  const currentStatus = parseStatus(order?.status);
  const [status, setStatus] = useState(() => parseStatus(initialStatus) ?? currentStatus);
  const [comment, setComment] = useState('');
  const [holdReason, setHoldReason] = useState('REPAIR');
  const [conditions, setConditions] = useState({});

  // Reopening for a different order, or from a different trigger, starts clean —
  // a reason typed for one order must never ride along to the next.
  useEffect(() => {
    if (!open) return;
    const resolvedInitial = parseStatus(initialStatus);
    const resolvedCurrent = parseStatus(order?.status);
    setStatus(resolvedInitial ?? resolvedCurrent ?? null);
    setComment('');
    setHoldReason(
      order?.hold?.reason && HOLD_REASONS.some((r) => r.value === order.hold.reason)
        ? order.hold.reason
        : 'REPAIR'
    );
    setConditions({});
  }, [open, initialStatus, order?.id, order?.status, order?.hold?.reason]);

  const options = useMemo(() => {
    const settable = settableStatuses(permissions);
    const curr = parseStatus(order?.status);
    const activeVal = parseStatus(status);

    const list = [...settable];
    if (curr != null && !list.some((s) => s.value === curr)) {
      list.unshift(statusMeta(curr, darkMode));
    }
    if (activeVal != null && !list.some((s) => s.value === activeVal)) {
      list.push(statusMeta(activeVal, darkMode));
    }

    return list.map((s) => {
      const meta = statusMeta(s.value, darkMode);
      const isCurrent = s.value === curr;
      return {
        value: s.value,
        searchValue: s.label,
        // The trigger is what renders inside the Select input box when chosen.
        // It uses house text color and inline alignment so it never breaks line-height
        // or collides with the chevron arrow.
        trigger: (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                background: meta.color,
                display: 'inline-block',
                flexShrink: 0,
              }}
            />
            <span
              style={{
                font: '500 13px Outfit, sans-serif',
                color: t.text,
              }}
            >
              {s.label}
            </span>
          </span>
        ),
        // The label is what renders in the dropdown menu list options.
        label: (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              gap: 8,
              padding: '2px 0',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  background: meta.color,
                  flexShrink: 0,
                }}
              />
              <span style={{ font: '500 13px Outfit, sans-serif', color: t.text }}>
                {s.label}
              </span>
            </div>
            {isCurrent && (
              <span
                style={{
                  font: '500 11px Outfit, sans-serif',
                  color: t.dim,
                  background: t.surface2,
                  border: `1px solid ${t.border}`,
                  padding: '1px 6px',
                  borderRadius: 6,
                }}
              >
                Current
              </span>
            )}
          </div>
        ),
      };
    });
  }, [permissions, order?.status, status, darkMode, t]);

  const products = order?.products || [];
  const heldFrom = order?.heldFromStatus != null ? Number(order.heldFromStatus) : null;
  const needsReason = status === HOLD ? holdReason === 'OTHER' : requiresReason(status);

  // Held orders resolve through where they were held from: cancelling one
  // parked at the printers or on the line has no finished goods to put back, so
  // it asks for a reason and nothing else. Held from READY onwards, it asks per
  // line, exactly as an unheld order in that status would.
  const needsLines =
    [CANCELLED, RETURNED].includes(status) && restocksFrom(currentStatus, heldFrom);
  const reasonGiven = comment.trim().length > 0;
  const linesGiven = !needsLines || products.every((p) => conditions[p.id]);

  const isRelease = currentStatus === HOLD && status !== HOLD;

  // What is still missing, said once next to the button that is waiting for it.
  const blocker = (() => {
    if (!status || status === currentStatus) return 'Choose a different status.';
    if (needsReason && !reasonGiven) {
      return status === HOLD
        ? 'Please specify a reason when choosing Other.'
        : 'A reason is required for this status.';
    }
    if (!linesGiven) {
      const left = products.filter((p) => !conditions[p.id]).length;
      return `Mark ${left} more line${left === 1 ? '' : 's'} Sellable or Damaged.`;
    }
    return null;
  })();

  const submit = () => {
    onSubmit({
      newStatus: status,
      statusComment: comment.trim(),
      holdReason: status === HOLD ? holdReason : undefined,
      lines: needsLines
        ? products.map((p) => ({ productId: p.id, condition: conditions[p.id] }))
        : undefined,
    });
  };

  return (
    <ConfigProvider theme={shellAntdTheme(darkMode)}>
      <Modal
        title={(
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            {isRelease ? (
              <UndoOutlined style={{ fontSize: 17, color: PRIMARY }} />
            ) : (
              <SwapOutlined style={{ fontSize: 17, color: PRIMARY }} />
            )}
            <span style={{ font: '700 16px Outfit, sans-serif', color: t.text }}>
              {isRelease ? 'Release order from hold' : 'Update order status'}
            </span>
          </div>
        )}
        open={open}
        onCancel={onCancel}
        width={520}
        destroyOnClose
        style={themeVars}
        wrapClassName="et-controls"
        className="et-controls et-status-modal"
        footer={(
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              paddingTop: 8,
              borderTop: `1px solid ${t.border}`,
            }}
          >
            <div style={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
              {blocker && (
                <div
                  style={{
                    font: '500 12px Outfit, sans-serif',
                    color: blocker === 'Choose a different status.' ? t.dim : STATUS.errorSoft,
                    lineHeight: '1.4',
                  }}
                >
                  {blocker}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              <button
                type="button"
                onClick={onCancel}
                disabled={submitting}
                style={{
                  height: 36,
                  padding: '0 14px',
                  borderRadius: 8,
                  border: `1px solid ${t.border}`,
                  background: t.surface2,
                  color: t.text,
                  font: '600 12.5px Outfit, sans-serif',
                  cursor: 'pointer',
                  transition: 'all .12s ease',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={blocker !== null || submitting}
                style={{
                  height: 36,
                  padding: '0 16px',
                  borderRadius: 8,
                  border: 'none',
                  background: EXIT_STATUSES.includes(status) ? STATUS.error : PRIMARY,
                  color: '#fff',
                  font: '600 12.5px Outfit, sans-serif',
                  cursor: blocker !== null || submitting ? 'not-allowed' : 'pointer',
                  opacity: blocker !== null || submitting ? 0.45 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  boxShadow: blocker === null && !submitting ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
                  transition: 'all .15s ease',
                }}
              >
                {submitting && (
                  <span
                    style={{
                      width: 12,
                      height: 12,
                      border: '2px solid rgba(255,255,255,0.4)',
                      borderTopColor: '#fff',
                      borderRadius: '50%',
                      animation: 'et-spin 0.8s linear infinite',
                      display: 'inline-block',
                    }}
                  />
                )}
                <span>{isRelease ? 'Release' : 'Update status'}</span>
              </button>
            </div>
          </div>
        )}
      >
        <style>{`
          .et-status-modal {
            font-family: Outfit, system-ui, sans-serif;
            color: var(--text);
          }
          .et-status-modal .ant-select:not(.ant-select-customize-input) .ant-select-selector {
            background: var(--surface2) !important;
            border: 1px solid var(--border) !important;
            border-radius: 8px !important;
            height: 38px !important;
            display: flex !important;
            align-items: center !important;
            padding: 0 11px !important;
          }
          .et-status-modal .ant-select-single .ant-select-selector .ant-select-selection-item {
            display: flex !important;
            align-items: center !important;
            line-height: normal !important;
            font-family: Outfit, system-ui, sans-serif !important;
            font-size: 13px !important;
            font-weight: 500 !important;
            color: var(--text) !important;
          }
          .et-status-modal .ant-select-single .ant-select-selector .ant-select-selection-placeholder {
            display: flex !important;
            align-items: center !important;
            line-height: normal !important;
            font-family: Outfit, system-ui, sans-serif !important;
            font-size: 13px !important;
            color: var(--dim) !important;
          }
          .et-status-modal .ant-select-arrow {
            color: var(--dim) !important;
          }
          .et-status-modal .ant-input,
          .et-status-modal .ant-input-affix-wrapper {
            background: var(--surface2) !important;
            border: 1px solid var(--border) !important;
            border-radius: 8px !important;
            color: var(--text) !important;
            font-family: Outfit, system-ui, sans-serif !important;
            font-size: 13px !important;
          }
          .et-status-modal .ant-input-affix-wrapper > .ant-input {
            background: transparent !important;
          }
          .et-status-modal .ant-input::placeholder {
            color: var(--dim) !important;
          }
          .et-status-modal .ant-input:focus,
          .et-status-modal .ant-input-focused,
          .et-status-modal .ant-input-affix-wrapper-focused,
          .et-status-modal .ant-select-focused:not(.ant-select-disabled) .ant-select-selector {
            border-color: var(--primary) !important;
            box-shadow: 0 0 0 3px var(--focusRing, rgba(217, 119, 6, 0.12)) !important;
          }
          @keyframes et-spin { to { transform: rotate(360deg); } }
        `}</style>

        <div className="et-controls" style={{ ...themeVars, display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 6 }}>
          {/* Order Context Strip */}
          {order && (
            <div
              style={{
                background: t.surface2,
                border: `1px solid ${t.border}`,
                borderRadius: 10,
                padding: '10px 14px',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                  <span style={{ font: '700 14.5px Outfit, sans-serif', color: t.text }}>
                    Order #{order.id}
                  </span>
                  {(order.customer?.name || order.marketplace?.name || order.orderable?.name) && (
                    <span
                      style={{
                        font: '400 12.5px Outfit, sans-serif',
                        color: t.muted,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      · {order.customer?.name || order.marketplace?.name || order.orderable?.name}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                  <span style={{ font: '500 11.5px Outfit, sans-serif', color: t.dim }}>
                    Current:
                  </span>
                  <StatusBadge status={currentStatus} />
                </div>
              </div>

              {/* If held: clear callout showing where it was held from and release option */}
              {currentStatus === HOLD && heldFrom && (
                <div
                  style={{
                    borderLeft: `3px solid #86198f`,
                    background: t.hover,
                    borderRadius: '0 8px 8px 0',
                    padding: '6px 10px',
                    font: '400 12px Outfit, sans-serif',
                    color: t.muted,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span>Held from</span>
                    <strong style={{ color: t.text }}>
                      {statusMeta(heldFrom, darkMode).label}
                    </strong>
                    <span>— releasing sends it back there.</span>
                    {order.hold?.days != null && (
                      <span style={{ color: t.dim }}>
                        ({order.hold.days === 0 ? 'held today' : `held ${order.hold.days}d`})
                      </span>
                    )}
                  </div>
                  {status !== heldFrom && (
                    <button
                      type="button"
                      onClick={() => setStatus(heldFrom)}
                      style={{
                        padding: '2px 8px',
                        borderRadius: 6,
                        border: `1px solid ${hexToRgba(PRIMARY, 0.4)}`,
                        background: 'transparent',
                        color: PRIMARY,
                        font: '600 11px Outfit, sans-serif',
                        cursor: 'pointer',
                      }}
                    >
                      Select {statusMeta(heldFrom, darkMode).label}
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Target Status Select */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label
                style={{
                  font: '700 11px Outfit, sans-serif',
                  color: t.dim,
                  textTransform: 'uppercase',
                  letterSpacing: '.05em',
                }}
              >
                Target Status
              </label>
              {status === currentStatus && (
                <span style={{ font: '400 11px Outfit, sans-serif', color: t.dim }}>
                  Select a new status to proceed
                </span>
              )}
            </div>
            <Select
              value={status}
              onChange={setStatus}
              optionLabelProp="trigger"
              options={options}
              style={{ width: '100%' }}
              placeholder="Choose a status"
            />
          </div>

          {/* Hold Reason Category (when moving to or on HOLD) */}
          {status === HOLD && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <label
                style={{
                  font: '700 11px Outfit, sans-serif',
                  color: t.dim,
                  textTransform: 'uppercase',
                  letterSpacing: '.05em',
                }}
              >
                Hold Reason Category
              </label>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: 4,
                  borderRadius: 8,
                  background: t.surface2,
                  border: `1px solid ${t.border}`,
                  flexWrap: 'wrap',
                }}
              >
                {HOLD_REASONS.map((r) => {
                  const on = holdReason === r.value;
                  return (
                    <button
                      key={r.value}
                      type="button"
                      onClick={() => setHoldReason(r.value)}
                      style={{
                        height: 30,
                        padding: '0 12px',
                        borderRadius: 6,
                        border: 'none',
                        background: on ? PRIMARY : 'transparent',
                        color: on ? '#fff' : t.muted,
                        font: `${on ? '600' : '500'} 12px Outfit, sans-serif`,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all .12s ease',
                      }}
                    >
                      {r.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Comment / Reason field */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <label
                style={{
                  font: '700 11px Outfit, sans-serif',
                  color: t.dim,
                  textTransform: 'uppercase',
                  letterSpacing: '.05em',
                }}
              >
                {needsReason ? 'Reason (Required)' : 'Comment (Optional)'}
              </label>
              {needsReason && (
                <span style={{ font: '500 11px Outfit, sans-serif', color: STATUS.errorSoft }}>
                  {status === HOLD ? 'Required when choosing Other' : 'Required for exit statuses'}
                </span>
              )}
            </div>
            <Input.TextArea
              rows={3}
              placeholder={
                needsReason
                  ? status === HOLD
                    ? 'Please specify a reason for this hold (required)...'
                    : 'Explain why this order is being cancelled or returned (required)...'
                  : 'Add an optional note to the order history...'
              }
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={200}
              showCount={needsReason}
              style={{
                font: '400 13px Outfit, sans-serif',
                borderRadius: 8,
              }}
            />
          </div>

          {/* Finished Goods Restock Decision */}
          {needsLines && (
            <div
              style={{
                background: t.surface2,
                border: `1px solid ${t.border}`,
                borderRadius: 10,
                padding: '12px 14px',
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <InfoCircleOutlined
                  style={{ color: PRIMARY, fontSize: 14, marginTop: 2, flexShrink: 0 }}
                />
                <div
                  style={{
                    font: '400 12px Outfit, sans-serif',
                    color: t.muted,
                    lineHeight: '1.45',
                  }}
                >
                  This order has finished goods. Mark each line <strong>Sellable</strong> to add it to
                  Ready Stock, or <strong>Damaged</strong> to write it off.
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {products.map((p) => {
                  const cond = conditions[p.id];
                  return (
                    <div
                      key={p.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12,
                        padding: '8px 10px',
                        background: t.surface,
                        border: `1px solid ${cond ? t.border : hexToRgba(STATUS.warn, 0.4)}`,
                        borderRadius: 8,
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          minWidth: 0,
                          flex: 1,
                        }}
                      >
                        <div
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: 6,
                            background: t.surface2,
                            color: t.muted,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            font: '700 12px Outfit, sans-serif',
                            flexShrink: 0,
                          }}
                        >
                          {p.productType?.name?.[0] || 'P'}
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div
                            style={{
                              font: '600 12.5px Outfit, sans-serif',
                              color: t.text,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {lineLabel(p)}
                          </div>
                          <div style={{ font: `500 11px ${MONO}`, color: t.dim }}>
                            Qty {p.unit} · {fmtBDT(p.price)}
                          </div>
                        </div>
                      </div>

                      {/* Segmented condition options */}
                      <div
                        style={{
                          display: 'flex',
                          gap: 4,
                          padding: 2,
                          borderRadius: 7,
                          background: t.surface2,
                          border: `1px solid ${t.border}`,
                          flexShrink: 0,
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => setConditions((prev) => ({ ...prev, [p.id]: 'SELLABLE' }))}
                          style={{
                            height: 26,
                            padding: '0 8px',
                            borderRadius: 5,
                            border: 'none',
                            background:
                              cond === 'SELLABLE' ? hexToRgba(STATUS.ok, 0.2) : 'transparent',
                            color: cond === 'SELLABLE' ? STATUS.ok : t.muted,
                            font: '600 11.5px Outfit, sans-serif',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            transition: 'all .1s ease',
                          }}
                        >
                          {cond === 'SELLABLE' && <CheckCircleFilled style={{ fontSize: 10 }} />}
                          Sellable
                        </button>
                        <button
                          type="button"
                          onClick={() => setConditions((prev) => ({ ...prev, [p.id]: 'DAMAGED' }))}
                          style={{
                            height: 26,
                            padding: '0 8px',
                            borderRadius: 5,
                            border: 'none',
                            background:
                              cond === 'DAMAGED' ? hexToRgba(STATUS.error, 0.18) : 'transparent',
                            color: cond === 'DAMAGED' ? STATUS.error : t.muted,
                            font: '600 11.5px Outfit, sans-serif',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            transition: 'all .1s ease',
                          }}
                        >
                          {cond === 'DAMAGED' && <CheckCircleFilled style={{ fontSize: 10 }} />}
                          Damaged
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </Modal>
    </ConfigProvider>
  );
}
